/**
 * Rules for when a 401 should clear Descope session + full redirect.
 * Kept separate from api-client for unit tests and clarity.
 */

export function getRequestUrlFromApiError(error: unknown): string | null {
  if (!error || typeof error !== 'object' || !('requestUrl' in error)) {
    return null;
  }
  const url = (error as { requestUrl?: unknown }).requestUrl;
  if (typeof url !== 'string' || url.length === 0) {
    return null;
  }
  return url;
}

/**
 * True when the failed request is the post-login audit (`POST /account/login`).
 * Axios `config.url` may be `account/login`, `/account/login`, or an absolute URL.
 */
function normalizeRequestPathForAuditCheck(withoutQuery: string): string {
  if (/^https?:\/\//i.test(withoutQuery)) {
    try {
      return new URL(withoutQuery).pathname;
    } catch {
      // fall through to relative handling
    }
  }
  return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
}

export function isLoginAuditRequestUrl(requestUrl: string): boolean {
  const withoutQuery = requestUrl.split('?')[0] ?? '';
  const path = normalizeRequestPathForAuditCheck(withoutQuery);
  return path === '/account/login' || path.endsWith('/account/login');
}

/**
 * Whether to clear DS and redirect on 401.
 *
 * On landing (`/`), we avoid global logout when we cannot attribute the 401 to a
 * specific request (no `requestUrl`) — same safety as the legacy `pathname !== '/'`
 * guard. We also skip when the failing call is the login audit (401 should not
 * wipe a brand-new Descope session).
 *
 * Off landing, missing `requestUrl` still triggers logout (session likely invalid).
 */
export function shouldTriggerGlobal401Logout(error: unknown): boolean {
  const requestUrl = getRequestUrlFromApiError(error);
  const onLanding =
    typeof window !== 'undefined' && window.location.pathname === '/';

  if (onLanding) {
    if (requestUrl === null) {
      return false;
    }
    return !isLoginAuditRequestUrl(requestUrl);
  }

  if (requestUrl === null) {
    return true;
  }
  return !isLoginAuditRequestUrl(requestUrl);
}
